import { ImportSheet } from "./Voices.js";
import { LevelUp } from "./Rewards.js";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  emptyDetails,
  type Character,
  type CharacterDetails,
  type CharacterSheet,
  type RollInput,
  type RollMode,
} from "@dm/shared";
import { api, type SheetPatch } from "./api.js";
import { useGame } from "./game.js";
import { useRoller } from "./Dice.js";
import { SECTIONS, useSheetPrefs } from "./sheetPrefs.js";
import type { SheetCtx } from "./sheet/context.js";
import {
  AbilitiesSection,
  AttacksSection,
  CombatSection,
  ConditionsSection,
  EquipmentSection,
  FeaturesSection,
  HeaderSection,
  NotesSection,
  PersonalitySection,
  ProficienciesSection,
  RollModeBar,
  SkillsSection,
  SpellsSection,
} from "./sheet/sections.js";

const blankSheet = (): CharacterSheet => ({
  name: "",
  race: "",
  className: "",
  level: 1,
  abilities: { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 },
  maxHp: 10,
  currentHp: 10,
  tempHp: 0,
  ac: 10,
  speed: 30,
  proficiencyBonus: 2,
  saveProficiencies: [],
  skillProficiencies: [],
  inventory: [],
  notes: "",
  details: { ...emptyDetails(), hitDice: [{ die: 8, total: 1, remaining: 1 }] },
});

export const toSheet = (c: Character): CharacterSheet => ({
  name: c.name,
  race: c.race,
  className: c.className,
  level: c.level,
  abilities: { ...c.abilities },
  maxHp: c.maxHp,
  currentHp: c.currentHp,
  tempHp: c.tempHp,
  ac: c.ac,
  speed: c.speed,
  proficiencyBonus: c.proficiencyBonus,
  saveProficiencies: [...c.saveProficiencies],
  skillProficiencies: [...c.skillProficiencies],
  inventory: [...c.inventory],
  notes: c.notes,
  details: c.details,
});

const SECTION_BODIES: Record<string, (p: { c: SheetCtx }) => ReactNode> = {
  header: HeaderSection,
  combat: CombatSection,
  conditions: ConditionsSection,
  abilities: AbilitiesSection,
  skills: SkillsSection,
  proficiencies: ProficienciesSection,
  attacks: AttacksSection,
  spells: SpellsSection,
  features: FeaturesSection,
  equipment: EquipmentSection,
  personality: PersonalitySection,
  notes: NotesSection,
};

type SaveState = "idle" | "pending" | "saving" | "saved" | "error";

/**
 * Full 5e character sheet. `characterId` undefined = your own active character (or create one).
 * Hosts can edit anyone's; players can view others' read-only. Edits autosave (only the changed fields).
 * Section visibility, collapse state and order are the *viewer's* preferences, stored on their account.
 */
export function CharacterPage({ characterId }: { characterId?: number }) {
  const { campaign, user, isHost, fail, refreshParty, party } = useGame();
  const [character, setCharacter] = useState<Character | null>(null);
  const [retired, setRetired] = useState<Character[]>([]);
  const [sheet, setSheet] = useState<CharacterSheet>(blankSheet);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [creating, setCreating] = useState(false);
  const [mode, setMode] = useState<RollMode>("normal");
  const [shortRest, setShortRest] = useState<string[] | null>(null);
  const [customize, setCustomize] = useState(false);
  const { roll: serverRoll, busy } = useRoller();
  const sp = useSheetPrefs();

  // Autosave plumbing: changed fields accumulate in `pending` and are PATCHed after a short pause.
  const pending = useRef<SheetPatch>({});
  const timer = useRef<number | undefined>(undefined);
  const inflight = useRef<Promise<void> | null>(null);
  const charRef = useRef<Character | null>(null);
  charRef.current = character;
  const lastSaved = useRef<string | null>(null);

  const hasPending = () => Object.keys(pending.current).length > 0;

  const flush = useCallback(async (): Promise<void> => {
    window.clearTimeout(timer.current);
    if (inflight.current) await inflight.current;
    const ch = charRef.current;
    if (!ch || !hasPending()) return;
    const patch = pending.current;
    pending.current = {};
    setSaveState("saving");
    const p = api
      .patchCharacter(campaign.id, ch.id, patch)
      .then((c) => {
        lastSaved.current = c.updatedAt;
        setCharacter(c);
        setSaveState(hasPending() ? "pending" : "saved");
      })
      .catch((err) => {
        // Put the failed changes back (newer edits win) so the next save retries them.
        pending.current = { ...patch, ...pending.current, details: { ...patch.details, ...pending.current.details } };
        setSaveState("error");
        fail(err);
      });
    inflight.current = p.finally(() => {
      inflight.current = null;
    });
    await inflight.current;
  }, [campaign.id, fail]);

  const schedule = () => {
    setSaveState("pending");
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void flush(), 700);
  };

  const update = (p: Partial<CharacterSheet>) => {
    setSheet((s) => ({ ...s, ...p }));
    if (!charRef.current) return;
    Object.assign(pending.current, p);
    schedule();
  };
  const updateDetails = (p: Partial<CharacterDetails>) => {
    setSheet((s) => ({ ...s, details: { ...s.details, ...p } }));
    if (!charRef.current) return;
    pending.current.details = { ...pending.current.details, ...p };
    schedule();
  };

  // Save on leaving the page / closing the tab.
  useEffect(() => {
    const onUnload = () => {
      const ch = charRef.current;
      if (ch && hasPending()) {
        void fetch(`/api/campaigns/${campaign.id}/characters/${ch.id}`, {
          method: "PATCH",
          keepalive: true,
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(pending.current),
        });
        pending.current = {};
      }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.removeEventListener("beforeunload", onUnload);
      void flush();
    };
  }, [campaign.id, flush]);

  const load = async () => {
    await flush();
    setLoading(true);
    try {
      if (characterId) {
        const c = await api.character(campaign.id, characterId);
        setCharacter(c);
        setSheet(toSheet(c));
        setRetired([]);
      } else {
        const mine = await api.myCharacters(campaign.id);
        setCharacter(mine.active);
        setSheet(mine.active ? toSheet(mine.active) : blankSheet());
        setRetired(mine.retired);
      }
      setSaveState("idle");
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [characterId, campaign.id]);

  // Live changes from elsewhere (DM damage, conditions, slots, another tab) refresh the sheet when you're not mid-edit.
  const live = character ? party.find((p) => p.id === character.id) : undefined;
  useEffect(() => {
    if (!live || hasPending() || inflight.current || live.updatedAt === lastSaved.current) return;
    if (character && live.updatedAt <= character.updatedAt && live.updatedAt !== character.updatedAt) return;
    setCharacter(live);
    setSheet(toSheet(live));
  }, [live?.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const canEdit = !character || isHost || character.ownerDiscordId === user.discordId;
  const ro = !canEdit;
  const canAct = !!character && character.status === "active" && canEdit;

  /** Server action that returns the updated character: save edits first, then take the server's sheet. */
  const serverAction = async (fn: (c: Character) => Promise<Character>) => {
    if (!character) return;
    await flush();
    try {
      const c = await fn(character);
      lastSaved.current = c.updatedAt;
      setCharacter(c);
      setSheet(toSheet(c));
      await refreshParty();
    } catch (err) {
      fail(err);
    }
  };

  const ctx: SheetCtx = {
    sheet,
    d: sheet.details,
    character,
    ro,
    canAct,
    update,
    updateDetails,
    busy,
    mode,
    setMode,
    playerName: character?.ownerName ?? user.displayName,
    roll: async (input: Omit<RollInput, "characterId" | "mode">, d20 = false) => {
      await flush(); // sheet checks use the saved sheet
      return serverRoll({ ...input, mode: d20 ? mode : "normal", characterId: character?.id });
    },
    spendHitDie: async (die: number) => {
      if (!character) return;
      await flush();
      try {
        const r = await api.spendHitDie(campaign.id, character.id, die);
        lastSaved.current = r.character.updatedAt;
        setCharacter(r.character);
        setSheet(toSheet(r.character));
        const healed = r.character.currentHp - character.currentHp;
        setShortRest((log) => (log ? [...log, `d${die}: rolled ${r.roll.dice[0]} ${r.roll.modifier >= 0 ? "+" : "−"} ${Math.abs(r.roll.modifier)} → +${healed} HP`] : log));
        await refreshParty();
      } catch (err) {
        fail(err);
      }
    },
  };

  const pendingAvatar = useRef<string | null>(null);
  const create = async () => {
    if (!sheet.name.trim()) return fail(new Error("Give your character a name"));
    setCreating(true);
    try {
      let c = await api.createCharacter(campaign.id, sheet);
      if (pendingAvatar.current) {
        c = await api.setAvatar(campaign.id, c.id, pendingAvatar.current).catch(() => c);
        pendingAvatar.current = null;
      }
      setCharacter(c);
      setSheet(toSheet(c));
      await refreshParty();
    } catch (err) {
      fail(err);
    } finally {
      setCreating(false);
    }
  };

  const setStatus = async (id: number, status: "active" | "retired") => {
    if (status === "retired" && !window.confirm("Retire this character? It is kept and can be made active again later.")) return;
    try {
      await flush();
      await api.setCharacterStatus(campaign.id, id, status);
      await refreshParty();
      if (characterId) window.location.hash = "#/character";
      else await load();
    } catch (err) {
      fail(err);
    }
  };

  const longRest = () => {
    if (!window.confirm("Long rest? Restores HP, spell slots, long-rest features and half your hit dice; clears temp HP and death saves; lowers exhaustion by 1.")) return;
    void serverAction((c) => api.rest(campaign.id, c.id, "long"));
  };
  const finishShortRest = async () => {
    await serverAction((c) => api.rest(campaign.id, c.id, "short"));
    setShortRest(null);
  };

  if (loading) return <main className="muted">Loading…</main>;

  const isNew = !character;
  const owner = character && character.ownerDiscordId !== user.discordId ? ` (${character.ownerName})` : "";
  const visible = sp.prefs.order.filter((id) => !sp.prefs.hidden.includes(id));
  const status =
    saveState === "saving" ? "Saving…" : saveState === "pending" ? "Unsaved…" : saveState === "saved" ? "Saved" : saveState === "error" ? "Save failed — will retry" : "";

  return (
    <main className="sheet">
      <div className="sheet-head">
        <h2>
          {isNew ? "New character" : sheet.name || "Character"}
          <span className="muted small">{owner}</span>
          {character?.status === "retired" && <span className="tag">retired</span>}
          {sheet.details.conditions.map((k) => (
            <span key={k} className="tag cond-tag">
              {k}
            </span>
          ))}
        </h2>
        <div className="sheet-actions">
          {!ro && !isNew && <span className={`small ${saveState === "error" ? "warn" : saveState === "saved" ? "ok" : "muted"}`}>{status}</span>}
          <RollModeBar c={ctx} />
          {character && character.status === "active" && canAct && <LevelUp character={character} onDone={() => void load().then(refreshParty)} />}
          {(isNew || (character && character.ownerDiscordId === user.discordId)) && (
            <a className={isNew ? "button primary wiz-btn" : "button wiz-btn"} href="#/wizard" title="Step-by-step character creation with the free SRD 5.1 options">
              🧙 Create with wizard
            </a>
          )}
          {(isNew || canAct) && (
            <ImportSheet
              campaignId={campaign.id}
              isNew={isNew}
              fail={fail}
              onUse={async (s, avatar) => {
                if (isNew) {
                  pendingAvatar.current = avatar;
                  return setSheet({ ...s, details: { ...s.details } });
                }
                try {
                  await flush();
                  await api.updateCharacter(campaign.id, character!.id, s);
                  if (avatar) await api.setAvatar(campaign.id, character!.id, avatar);
                  await load();
                  await refreshParty();
                } catch (err) {
                  fail(err);
                }
              }}
            />
          )}
          {character && canAct && (
            <span className="export-links small" title="Download this character to keep, print, or import into another campaign">
              Export:{" "}
              <a href={`/api/campaigns/${campaign.id}/characters/${character.id}/export.json`} download>JSON</a>{" · "}
              <a href={`/api/campaigns/${campaign.id}/characters/${character.id}/export.pdf`} download>PDF</a>
            </span>
          )}
          {canAct && (
            <>
              <button type="button" onClick={() => setShortRest(shortRest ? null : [])} title="Spend hit dice, recover short-rest features and pact slots">
                Short rest
              </button>
              <button type="button" onClick={longRest}>
                Long rest
              </button>
            </>
          )}
          {isNew && (
            <button className="primary" disabled={creating} onClick={create}>
              Create character
            </button>
          )}
          <div className="customize-wrap">
            <button type="button" onClick={() => setCustomize(!customize)} aria-expanded={customize}>
              ⚙ Customize sheet
            </button>
            {customize && (
              <div className="customize-menu" role="dialog" aria-label="Customize sheet">
                <p className="muted small">Your layout (saved to your account, applies to every sheet you open).</p>
                <ul>
                  {sp.prefs.order.map((id, i) => {
                    const def = SECTIONS.find((s) => s.id === id)!;
                    return (
                      <li key={id}>
                        <label>
                          <input type="checkbox" checked={!sp.prefs.hidden.includes(id)} onChange={() => sp.toggleHidden(id)} />
                          {def.title}
                        </label>
                        <span>
                          <button type="button" className="icon" disabled={i === 0} title="Move up" onClick={() => sp.move(id, -1)}>
                            ↑
                          </button>
                          <button type="button" className="icon" disabled={i === sp.prefs.order.length - 1} title="Move down" onClick={() => sp.move(id, 1)}>
                            ↓
                          </button>
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <div className="customize-actions">
                  <button type="button" className="small-btn" onClick={() => sp.setAllCollapsed(false)}>
                    Expand all
                  </button>
                  <button type="button" className="small-btn" onClick={() => sp.setAllCollapsed(true)}>
                    Collapse all
                  </button>
                  <button type="button" className="small-btn" onClick={sp.reset}>
                    Reset to default
                  </button>
                  <button type="button" className="small-btn primary" onClick={() => setCustomize(false)}>
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
          {!ro && character?.status === "active" && <button onClick={() => setStatus(character.id, "retired")}>Retire</button>}
        </div>
      </div>
      {ro && <p className="muted small">Read only: only {character?.ownerName} or the host can edit this character.</p>}

      {shortRest && character && (
        <section className="panel short-rest">
          <h3>Short rest</h3>
          <p className="small muted">Spend hit dice to heal (each rolls the die + your CON modifier), then finish the rest to recover short-rest features and pact slots.</p>
          <div className="hd-spend">
            {sheet.details.hitDice.filter((h) => h.total > 0).map((h, i) => (
              <button key={i} type="button" disabled={busy || h.remaining === 0 || sheet.currentHp >= sheet.maxHp} onClick={() => void ctx.spendHitDie(h.die)}>
                🎲 Spend d{h.die} ({h.remaining}/{h.total} left)
              </button>
            ))}
            {sheet.details.hitDice.length === 0 && <span className="muted small">No hit dice on the sheet (add them under Combat).</span>}
            <span className="small">
              HP {sheet.currentHp}/{sheet.maxHp}
            </span>
          </div>
          {shortRest.length > 0 && (
            <ul className="small">
              {shortRest.map((l, i) => (
                <li key={i}>{l}</li>
              ))}
            </ul>
          )}
          <div className="quick-row">
            <button type="button" className="primary" onClick={() => void finishShortRest()}>
              Finish short rest
            </button>
            <button type="button" onClick={() => setShortRest(null)}>
              Cancel
            </button>
          </div>
        </section>
      )}

      <div className="sheet-grid">
        {visible.map((id) => {
          const def = SECTIONS.find((s) => s.id === id)!;
          const Body = SECTION_BODIES[id]!;
          const collapsed = sp.prefs.collapsed.includes(id);
          return (
            <section key={id} className={`panel sheet-section s-${id} ${def.wide ? "wide" : ""} ${collapsed ? "collapsed" : ""}`}>
              <h3>
                <button type="button" className="collapse-btn" aria-expanded={!collapsed} onClick={() => sp.toggleCollapsed(id)}>
                  <span className="chev">{collapsed ? "▸" : "▾"}</span> {def.title}
                </button>
              </h3>
              {!collapsed && (
                <fieldset disabled={ro} className="section-body">
                  <Body c={ctx} />
                </fieldset>
              )}
            </section>
          );
        })}
        {visible.length === 0 && <p className="muted">Every section is hidden. Use “Customize sheet” to show some.</p>}
      </div>

      {!characterId && retired.length > 0 && (
        <section className="panel">
          <h3>Retired characters</h3>
          <ul className="retired">
            {retired.map((c) => (
              <li key={c.id}>
                <a href={`#/character/${c.id}`}>{c.name}</a>
                <span className="muted small">
                  {" "}
                  {[c.race, c.className].filter(Boolean).join(" ")} {c.level}
                </span>
                <button onClick={() => setStatus(c.id, "active")}>Make active</button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}
